import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, FadeUp } from "./Reveal";
import { WHATSAPP_LINK, TRUST_ITEMS, IMAGES } from "../../data/content";

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
    const photoY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section id="top" ref={ref} className="relative min-h-screen flex flex-col overflow-hidden bg-white" data-testid="hero-section">
            <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0 pointer-events-none" aria-hidden="true">
                <img src={IMAGES.abstract} alt="" className="w-full h-full object-cover opacity-[0.07]" loading="eager" />
                <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white" />
            </motion.div>

            <div className="relative z-10 flex-1 flex items-center max-w-7xl mx-auto w-full px-5 md:px-8 pt-36 pb-16 md:pt-40">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
                    <div className="lg:col-span-7">
                        <FadeUp y={20}>
                            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 mb-8">
                                <span className="w-2 h-2 rounded-full bg-accent" />
                                AI Growth Agency · Ludhiana, India
                            </p>
                        </FadeUp>

                        <MaskedLines
                            delay={0.15}
                            className="font-display font-semibold tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[5.25rem] text-navy"
                            lines={[
                                "From lead generation",
                                "to revenue automation,",
                                <span key="l3" className="text-accent">done for you.</span>,
                            ]}
                        />

                        <FadeUp delay={0.55}>
                            <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-slate-600">
                                ChatProz is a founder-led AI growth agency. We build, automate, and grow.
                                For <span className="font-semibold text-navy">businesses</span> that need a predictable sales
                                pipeline, and <span className="font-semibold text-navy">creators</span> who want to scale
                                without living on camera.
                            </p>
                        </FadeUp>

                        <FadeUp delay={0.7}>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <motion.a
                                    href={WHATSAPP_LINK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="hero-whatsapp-cta"
                                    whileTap={{ scale: 0.96 }}
                                    className="inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 text-base font-semibold text-white hover:bg-navy transition-colors duration-300"
                                >
                                    Chat on WhatsApp
                                    <ArrowUpRight className="w-5 h-5" />
                                </motion.a>
                                <a
                                    href="#contact"
                                    data-testid="hero-secondary-cta"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                                    }}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-8 py-4 text-base font-semibold text-navy hover:border-navy transition-colors duration-300"
                                >
                                    Tell us about your project
                                </a>
                            </div>
                        </FadeUp>
                    </div>

                    <div className="lg:col-span-5 flex justify-center lg:justify-end">
                        <motion.div
                            style={{ y: photoY }}
                            initial={{ opacity: 0, y: 60, rotate: 3 }}
                            animate={{ opacity: 1, y: 0, rotate: 2 }}
                            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="w-full max-w-sm"
                            data-testid="hero-founder-photo"
                        >
                            <div className="relative rounded-lg border border-slate-200 bg-mist aspect-[4/5] overflow-hidden shadow-[0_30px_60px_-30px_rgba(28,28,28,0.3)]">
                                <img
                                    src="/hero-kuunal.jpg"
                                    alt="Kuunal Pruuthi, Founder of ChatProz"
                                    className="absolute inset-0 w-full h-full object-cover object-top"
                                    loading="eager"
                                />
                                <div className="absolute bottom-0 inset-x-0 bg-navy/95 backdrop-blur-sm px-5 py-4">
                                    <p className="font-display font-semibold text-white text-lg leading-tight">Kuunal Pruuthi</p>
                                    <p className="text-white/60 text-xs uppercase tracking-[0.2em] mt-1">Founder, ChatProz</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            <div className="relative z-10 border-t border-slate-200 bg-white/80 backdrop-blur-sm" data-testid="hero-trust-strip">
                <Marquee speed={28} gradient={false} className="py-4">
                    {TRUST_ITEMS.map((item, i) => (
                        <span key={i} className="flex items-center gap-8 mx-8 text-xs md:text-sm font-medium uppercase tracking-[0.2em] text-slate-500 whitespace-nowrap">
                            {item}
                            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                        </span>
                    ))}
                </Marquee>
            </div>
        </section>
    );
};

export default Hero;
